/**
 * Tenant-level merchandising order: which categories a storefront opens on.
 *
 * A seller configures an ordered list of taxonomy ids in
 * `storefront.productPriority` (portal-api's per-tenant landing config).
 * Farm Fresh, for example, carries `["264"]` — "All Vegetables" — so a produce
 * store opens on produce rather than on whatever happens to be newest.
 *
 * The product service implements the same ordering server-side via
 * `GET /api/products?priority=<ids>`; this is the client-side counterpart for
 * the landing payload, which arrives via portal-api and therefore can't carry
 * the query param. Both rank by the SAME rule so a buyer sees one order.
 *
 * A product matches on its category OR any of its subcategories, so a priority
 * entry can name a platform category, a department, or a leaf.
 */

/** Rank given to a product matching no configured id — sorts after every match. */
const NO_PRIORITY_RANK = Number.MAX_SAFE_INTEGER;

export interface PriorityRankable {
  categoryId?: string;
  subcategoryIds?: string[];
}

/**
 * Build a comparator-friendly ranker. Returns the best (lowest) position the
 * product matches, so a product tagged both Vegetables and Fruits sorts under
 * whichever the seller listed first.
 *
 * Returns `null` when nothing is configured, letting callers skip the key
 * entirely rather than pay for a no-op sort.
 */
export function buildPriorityRanker(
  priority?: string[] | null
): ((p: PriorityRankable) => number) | null {
  const ids = (priority ?? []).map(String).filter(Boolean);
  if (!ids.length) return null;

  const rankById = new Map<string, number>();
  ids.forEach((id, i) => {
    // First occurrence wins, so a duplicated id can't demote itself.
    if (!rankById.has(id)) rankById.set(id, i);
  });

  return (p: PriorityRankable): number => {
    let best = NO_PRIORITY_RANK;
    const catRank = p.categoryId ? rankById.get(p.categoryId) : undefined;
    if (catRank !== undefined) best = catRank;
    for (const sub of p.subcategoryIds ?? []) {
      const r = rankById.get(sub);
      if (r !== undefined && r < best) best = r;
    }
    return best;
  };
}
